import React, { useEffect, useRef, useState } from 'react';
import styles from './Video.module.scss';
import classnames from 'classnames';
import moment from 'moment';
import Hammer from 'react-hammerjs';
import { isIOS, isMobile, isTablet } from 'react-device-detect';
import { ChannelsFullNames } from 'src/modules/locale';

export interface VideoProps {
  channel?: ChannelsFullNames;
  video?: {
    url: string;
    ext: string;
    mime: string;
  };
  cover?: {
    url: string;
  };
  caption: string;
}

const loopPreviewSeconds = 10;

const secondsToMinutes = (seconds: number) => {
  return moment.utc(seconds * 1000).format('mm:ss');
};

const Video: React.FunctionComponent<VideoProps> = (props: VideoProps) => {
  const [hack, setHack] = useState<string>('');
  const [controlsEnabled, setControlsEnabled] = useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(false);
  const [isFullscreen, setFullscreen] = useState<boolean>(false);
  const [isMute, setMute] = useState(true);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState('00:00');
  const [seeking, setSeeking] = useState(false);
  const progressContainerRef = useRef(null);

  const videoRef = useRef();
  const videoContainerRef = useRef();

  const {
    video: { url, mime },
    cover,
    caption,
  } = props;

  const coverUrl = cover?.url;

  useEffect(() => {
    setHack('hack');
  }, []);

  const updateCurrentTime = () => {
    const video = videoRef.current as any;
    setCurrentTime(secondsToMinutes(video.currentTime));
  };

  useEffect(() => {
    const video = videoRef.current as any;

    const onLoadedMetadata = () => {
      setDuration(secondsToMinutes(video.duration));
    };

    const onPause = () => {
      setPlaying(false);
    };

    const onPlay = () => {
      if (controlsEnabled) {
        setPlaying(true);
      }
    };

    // video metadata might already be loaded at this point
    if (video.readyState >= 2) {
      onLoadedMetadata();
    }
    // if the video metadata is not loaded, use the loadedmetadata event handler
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('pause', onPause);
    video.addEventListener('play', onPlay);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('play', onPlay);
    };
  }, [controlsEnabled]);

  const onTimeUpdate = () => {
    const video = videoRef.current as any;
    if (!controlsEnabled) {
      if (video.currentTime > loopPreviewSeconds) {
        video.pause();
        video.currentTime = 0;
        video.play();
      }
    } else {
      updateCurrentTime();
      setProgress((video.currentTime * 100) / video.duration);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current as any;
    if (isTablet && video?.webkitEnterFullScreen) video.webkitEnterFullScreen();
    if (!controlsEnabled) {
      video.pause();
      setControlsEnabled(true);
      setMute(false);
      video.currentTime = 0;
      video.muted = false;
      video.autoplay = false;
      video.play();
    } else {
      if (!playing) {
        video.play();
      } else {
        // TODO check if video is at the end
        video.pause();
      }
    }
  };

  const toggleFullscreen = () => {
    const videoContainer = videoContainerRef.current as any;
    if (isFullscreen) {
      setFullscreen(false);
      document.exitFullscreen();
    } else {
      setFullscreen(true);
      videoContainer.requestFullscreen();
    }
  };

  const toggleVolume = () => {
    const video = videoRef.current as any;
    if (isMute) {
      setMute(false);
      video.muted = false;
    } else {
      setMute(true);
      video.muted = true;
    }
  };

  const updateTimeFromEvent = (e) => {
    const video = videoRef.current as any;

    const progressContainer = progressContainerRef.current;

    const progressContainerBoundingBox = progressContainer.getBoundingClientRect();
    const newProgress =
      ((e.center.x - progressContainerBoundingBox.x) * 100) / progressContainerBoundingBox.width;
    setProgress(newProgress);
    video.currentTime = (newProgress * video.duration) / 100;
  };

  const onSeekStart = (e) => {
    updateTimeFromEvent(e);
    const video = videoRef.current as any;

    setSeeking(true);
    video.pause();

    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const onSeek = (e) => {
    if (seeking) {
      updateTimeFromEvent(e);
    }

    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const onSeekEnd = (e, forcePlay) => {
    if (seeking || forcePlay) {
      const video = videoRef.current as any;

      setSeeking(false);
      video.play();
    }

    e.srcEvent.preventDefault();
    e.srcEvent.stopPropagation();
  };

  const videoControls = (
    <div className={styles.controls}>
      <div className={styles.togglePlayButton} onClick={togglePlay} />
      <Hammer
        onTap={(e) => {
          onSeekStart(e);
          onSeekEnd(e, true);
        }}
        onPress={onSeekStart}
        onPanStart={onSeekStart}
        onPan={onSeek}
        onPanEnd={onSeekEnd}
        onPressUp={onSeekEnd}>
        <div className={styles.progressContainerHammerWrapper}>
          <div ref={progressContainerRef} className={styles.progressContainer}>
            <div className={styles.progressBarContainer}>
              <div
                className={styles.progressBar}
                style={{
                  width: `${progress}%`,
                }}
              />
            </div>
          </div>
        </div>
      </Hammer>
      <div className={styles.time}>
        {currentTime} | {duration}
      </div>
      <div className={styles.toggleVolume} onClick={toggleVolume} />
      <div className={styles.toggleFullscreen} onClick={toggleFullscreen} />
    </div>
  );

  return (
    <div
      className={classnames(
        styles.container,
        playing ? styles.playing : styles.paused,
        controlsEnabled ? styles.controlsEnabled : styles.controlsDisabled,
        isMute ? styles.mute : styles.notMute,
        isFullscreen ? styles.fullscreen : styles.notFullscreen
      )}
      key={`video-${props.video?.url}-${hack}`}>
      <div ref={videoContainerRef} className={styles.videoContainer}>
        <div className={isMobile ? styles.mobilePlayButton : undefined} onClick={togglePlay} />
        {!isIOS && videoControls}
        <video
          poster={coverUrl}
          ref={videoRef}
          muted={true}
          autoPlay={!isIOS}
          onClick={togglePlay}
          onTimeUpdate={onTimeUpdate}>
          <source src={url} type={mime} />
          Sorry, your browser doesn&apos;t support embedded videos.
        </video>
      </div>
      <div className={styles.caption} dangerouslySetInnerHTML={{ __html: caption }} />
    </div>
  );
};

export default Video;
